import { db } from '../config'
import { collection, query, where, getDocs, addDoc, deleteDoc, doc, getDoc } from 'firebase/firestore'

export interface UserOrganizationAccess {
  id: string
  userId: string
  organizationId: string
  role: 'admin' | 'manager' | 'user'
  createdAt: Date
  updatedAt: Date
}

interface UserOrganizationService {
  getUserOrganizations(userId: string): Promise<UserOrganizationAccess[]>
  getOrganizationUsers(organizationId: string): Promise<UserOrganizationAccess[]>
  addUserToOrganization(userId: string, organizationId: string, role: 'admin' | 'manager' | 'user'): Promise<UserOrganizationAccess>
  removeUserFromOrganization(accessId: string): Promise<void>
  checkUserAccess(userId: string, organizationId: string): Promise<UserOrganizationAccess | null>
  updateUserOrganizations(userId: string, organizationIds: string[]): Promise<void>
}

export const userOrganizationService: UserOrganizationService = {
  // Obter todas as organizações que um usuário tem acesso
  async getUserOrganizations(userId: string): Promise<UserOrganizationAccess[]> {
    const q = query(
      collection(db, 'user-organizations'),
      where('userId', '==', userId)
    )
    const snapshot = await getDocs(q)
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate()
    })) as UserOrganizationAccess[]
  },

  // Obter todos os usuários de uma organização
  async getOrganizationUsers(organizationId: string): Promise<UserOrganizationAccess[]> {
    const q = query(
      collection(db, 'user-organizations'),
      where('organizationId', '==', organizationId)
    )
    const snapshot = await getDocs(q)
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate()
    })) as UserOrganizationAccess[]
  },

  // Adicionar acesso de usuário a uma organização
  async addUserToOrganization(userId: string, organizationId: string, role: 'admin' | 'manager' | 'user'): Promise<UserOrganizationAccess> {
    const now = new Date()
    const docRef = await addDoc(collection(db, 'user-organizations'), {
      userId,
      organizationId,
      role,
      createdAt: now,
      updatedAt: now
    })
    
    return {
      id: docRef.id,
      userId,
      organizationId,
      role,
      createdAt: now,
      updatedAt: now
    }
  },

  // Remover acesso de usuário a uma organização
  async removeUserFromOrganization(accessId: string): Promise<void> {
    await deleteDoc(doc(db, 'user-organizations', accessId))
  },

  // Verificar se um usuário tem acesso a uma organização
  async checkUserAccess(userId: string, organizationId: string): Promise<UserOrganizationAccess | null> {
    const q = query(
      collection(db, 'user-organizations'),
      where('userId', '==', userId),
      where('organizationId', '==', organizationId)
    )
    const snapshot = await getDocs(q)
    if (snapshot.empty) return null

    const doc = snapshot.docs[0]
    return {
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate()
    } as UserOrganizationAccess
  },

  // Atualizar organizações do usuário
  async updateUserOrganizations(userId: string, organizationIds: string[]): Promise<void> {
    // Primeiro, buscar todas as organizações atuais do usuário
    const currentOrgs = await this.getUserOrganizations(userId)
    
    // Organizações para remover (estão em currentOrgs mas não em organizationIds)
    const orgsToRemove = currentOrgs.filter(org => !organizationIds.includes(org.organizationId))
    
    // Organizações para adicionar (estão em organizationIds mas não em currentOrgs)
    const orgsToAdd = organizationIds.filter(orgId => 
      !currentOrgs.some(org => org.organizationId === orgId)
    )
    
    // Remover organizações não selecionadas
    await Promise.all(
      orgsToRemove.map(org => this.removeUserFromOrganization(org.id))
    )
    
    // Adicionar novas organizações
    await Promise.all(
      orgsToAdd.map(orgId => this.addUserToOrganization(userId, orgId, 'user'))
    )
  }
}
